import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  Leaf, 
  TrendingUp, 
  Link2, 
  Monitor,
  ArrowRight 
} from 'lucide-react';

const ServiceCard = ({ id, title, description, tagline, Icon, colorClass, image, index, dedicatedPage }) => {
  const linkTo = dedicatedPage || `/services#${id}`;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group relative"
      data-testid={`service-card-${id}`}
    >
      <div className="service-card card-hover bg-white rounded-2xl overflow-hidden shadow-sm h-full border border-gray-100">
        {/* Image with artistic filter */}
        <div className="relative h-48 overflow-hidden">
          <img 
            src={image}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            style={{ filter: 'saturate(0.8) contrast(1.05)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
          
          {/* Title on image */}
          <div className="absolute bottom-4 left-4 right-4">
            <h3 className="font-serif text-xl text-white font-semibold">
              {title}
            </h3>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="font-sans text-gray-600 text-sm leading-relaxed mb-4">
            {description}
          </p>
          
          {/* Tagline */}
          <p className="font-serif text-sm text-brand-brown italic mb-5">
            {tagline}
          </p>

          <Link 
            to={linkTo}
            className="inline-flex items-center font-sans text-sm font-medium text-brand-green hover:gap-3 transition-all group/link"
          >
            Learn More
            <ArrowRight className="w-4 h-4 ml-1.5 transition-transform group-hover/link:translate-x-1" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

export const Services = () => {
  return (
    <section data-testid="services-section" className="py-16 md:py-20 bg-brand-surface">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12 md:mb-14"
        >
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-brand-dark mb-4">
            Sustainability Services
          </h2>
          <p className="font-sans text-base md:text-lg text-gray-600 max-w-2xl">
            Built for regulation, markets, and long-term value creation.
          </p>
        </motion.div>

        {/* Services Grid - 3 top, 2 bottom centered */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          <ServiceCard 
            id="esg"
            title="ESG Advisory"
            description="Develop ESG strategies grounded in material risks, governance maturity, due diligence, and disclosures-driven reporting."
            tagline="Strategy-led. Execution-focused."
            Icon={FileText}
            colorClass="bg-brand-green"
            image="https://images.unsplash.com/photo-1594080051162-74b97d619668?w=600&q=80"
            index={0}
            dedicatedPage="/contact"
          />
          <ServiceCard 
            id="carbon"
            title="Carbon Market Advisory"
            description="Structure high-integrity carbon project covering feasibility assessment, PDD development, MRV architecture, issuance pathways, pricing logic, and offtake readiness."
            tagline="On-ground projects. Market-ready integrity."
            Icon={Leaf}
            colorClass="bg-brand-brown"
            image="https://images.pexels.com/photos/26605341/pexels-photo-26605341.jpeg?w=600&q=80"
            index={1}
            dedicatedPage="/contact"
          />
          <ServiceCard 
            id="climate"
            title="Climate Finance"
            description="Unlock climate capital through investment-ready strategies, blended finance structures, and sustainability-linked instruments aligned with global standards."
            tagline="Where climate outcomes meet capital markets."
            Icon={TrendingUp}
            colorClass="bg-brand-green"
            image="https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=600&q=80"
            index={2}
            dedicatedPage="/contact"
          />
        </div>

        {/* Bottom 2 cards centered */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mt-6 lg:mt-8 max-w-4xl mx-auto">
          <ServiceCard 
            id="supply-chain"
            title="Sustainable Supply Chain"
            description="Enable supply chain traceability and decarbonization to manage Scope 3 risk, improve resilience, and meet global compliance requirements."
            tagline="Built for global value chains."
            Icon={Link2}
            colorClass="bg-brand-brown"
            image="https://images.pexels.com/photos/9716363/pexels-photo-9716363.jpeg?auto=compress&cs=tinysrgb&w=600"
            index={3}
            dedicatedPage="/contact"
          />
          <ServiceCard 
            id="digital"
            title="Digital Sustainability Solutions"
            description="Design customized ESG and dMRV platforms with automated data capture, traceability, and audit-ready outputs for carbon and supply-chain programs."
            tagline="Technology that supports credibility, not dashboards."
            Icon={Monitor}
            colorClass="bg-brand-green"
            image="https://images.pexels.com/photos/577210/pexels-photo-577210.jpeg?auto=compress&cs=tinysrgb&w=600"
            index={4}
            dedicatedPage="/contact"
          />
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-12"
        >
          <Link to="/services">
            <button 
              data-testid="services-view-all-button"
              className="btn-gradient text-white font-sans font-medium px-8 py-4 rounded-full inline-flex items-center shadow-lg hover:shadow-xl transition-shadow"
            >
              Explore All Services
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
